import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
} from "@tabipla/ui";
import { UserPlus, Receipt, CheckCircle2, ArrowRight, PartyPopper } from "lucide-react";

interface OnboardingGuideProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupName: string;
  onInviteMembers: () => void;
  onAddExpense: () => void;
  onSkip: () => void;
}

export function OnboardingGuide({
  open,
  onOpenChange,
  groupName,
  onInviteMembers,
  onAddExpense,
  onSkip,
}: OnboardingGuideProps) {
  const { t } = useTranslation();

  const steps = [
    {
      icon: UserPlus,
      title: t("onboarding.step1Title", "メンバーを招待"),
      description: t(
        "onboarding.step1Description",
        "LINEで招待リンクを送って、メンバーを追加しましょう"
      ),
      action: t("onboarding.inviteAction", "招待する"),
      onClick: onInviteMembers,
      recommended: true,
    },
    {
      icon: Receipt,
      title: t("onboarding.step2Title", "出費を追加"),
      description: t("onboarding.step2Description", "立て替えた出費を記録して、精算金額を計算"),
      action: t("onboarding.addExpenseAction", "出費を追加"),
      onClick: onAddExpense,
      recommended: false,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="sm:max-w-md">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600">
            <PartyPopper className="h-8 w-8 text-white" />
          </div>
          <DialogTitle className="text-xl">
            {t("onboarding.congratsTitle", "わりかんを作成しました！")}
          </DialogTitle>
          <DialogDescription>
            {t("onboarding.congratsDescription", "「{{groupName}}」の準備ができました", {
              groupName,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          <p className="text-center text-sm font-medium text-muted-foreground">
            {t("onboarding.nextSteps", "次のステップ")}
          </p>
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <button
                key={index}
                onClick={step.onClick}
                className="group flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-colors hover:border-emerald-300 hover:bg-emerald-50/50 active:bg-emerald-100/50 dark:hover:border-emerald-700 dark:hover:bg-emerald-900/20 dark:active:bg-emerald-900/40"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-400">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{step.title}</span>
                    {step.recommended && (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
                        {t("onboarding.recommended", "おすすめ")}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-sm text-muted-foreground">{step.description}</p>
                </div>
                <ArrowRight className="mt-2 h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1" />
              </button>
            );
          })}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <div className="text-center">
            <Button variant="ghost" className="w-full text-muted-foreground" onClick={onSkip}>
              {t("onboarding.skipForNow", "まずは一人で試してみる")}
            </Button>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("onboarding.skipHint", "メンバーはいつでも右上メニューから招待できます")}
            </p>
          </div>
        </DialogFooter>

        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 pb-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          <span className="text-xs text-muted-foreground">
            {t("onboarding.progress", "ステップ 1/3 完了")}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
